import { useMemo, useCallback, useEffect, useState } from 'react';
import type { Document } from '@kendraw/scene';
import { validateValence } from '@kendraw/scene';
import { computeProperties } from '@kendraw/chem';
import { exportToSVG, injectSvgMetadata } from '@kendraw/renderer-svg';
import { writeMolV2000 } from '@kendraw/io';
import { jsPDF } from 'jspdf';

interface BackendProps {
  exact_mass: number;
  canonical_smiles: string;
  inchi: string;
  inchi_key: string;
  logp: number | null;
  tpsa: number | null;
  hbd: number | null;
  hba: number | null;
  rotatable_bonds: number | null;
  lipinski_pass: boolean | null;
}

interface PropertyPanelProps {
  doc: Document;
  visible: boolean;
}

export function PropertyPanel({ doc, visible }: PropertyPanelProps) {
  const page = doc.pages[doc.activePageIndex];

  const properties = useMemo(() => {
    if (!page) return null;
    return computeProperties(page);
  }, [page]);

  const valenceIssueCount = useMemo(() => {
    if (!page) return 0;
    return validateValence(page).length;
  }, [page]);

  // Fetch backend-computed properties (LogP, tPSA, InChI, exact mass, etc.)
  const [backendProps, setBackendProps] = useState<BackendProps | null>(null);
  useEffect(() => {
    if (!page || Object.keys(page.atoms).length === 0) {
      setBackendProps(null);
      return;
    }
    const atoms = Object.values(page.atoms);
    const bonds = Object.values(page.bonds);
    const mol = writeMolV2000(atoms, bonds);
    let cancelled = false;
    const baseUrl = '/api';
    fetch(`${baseUrl}/convert/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ input_data: mol, input_format: 'mol', output_format: 'smiles' }),
    })
      .then((r) => r.json())
      .then((res: { success: boolean; output: string }) => {
        if (cancelled || !res.success) return;
        return fetch(`${baseUrl}/compute/properties/smiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ smiles: res.output }),
        });
      })
      .then((r) => r?.json())
      .then((props: BackendProps | undefined) => {
        if (!cancelled && props) setBackendProps(props);
      })
      .catch(() => {
        /* backend unavailable — silently skip */
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const handleExportSVG = useCallback(() => {
    if (!page) return;
    let svg = exportToSVG(page);
    svg = injectSvgMetadata(svg, {
      title: doc.metadata.title,
      author: doc.metadata.authorHint ?? 'Kendraw User',
    });
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    downloadBlob(blob, `${doc.metadata.title || 'molecule'}.svg`);
  }, [page, doc.metadata]);

  const handleExportMOL = useCallback(() => {
    if (!page) return;
    const atoms = Object.values(page.atoms);
    const bonds = Object.values(page.bonds);
    const mol = writeMolV2000(atoms, bonds);
    const blob = new Blob([mol], { type: 'chemical/x-mdl-molfile' });
    downloadBlob(blob, `${doc.metadata.title || 'molecule'}.mol`);
  }, [page, doc.metadata]);

  const handleExportPNG = useCallback(() => {
    if (!page) return;
    const svg = exportToSVG(page, { width: 1200, height: 900 });
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 900;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 1200, 900);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) downloadBlob(blob, `${doc.metadata.title || 'molecule'}.png`);
      }, 'image/png');
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }, [page, doc.metadata]);

  const handleExportPDF = useCallback(() => {
    if (!page) return;
    const svg = exportToSVG(page, { width: 1200, height: 900 });
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const dpi = 300;
      const scale = dpi / 72;
      canvas.width = 1200 * scale;
      canvas.height = 900 * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(scale, scale);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 1200, 900);
      ctx.drawImage(img, 0, 0, 1200, 900);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      // Center the image with margins
      const margin = 40;
      const availW = pdfW - 2 * margin;
      const availH = pdfH - 2 * margin;
      const imgAspect = 1200 / 900;
      const availAspect = availW / availH;
      let drawW: number;
      let drawH: number;
      if (imgAspect > availAspect) {
        drawW = availW;
        drawH = availW / imgAspect;
      } else {
        drawH = availH;
        drawW = availH * imgAspect;
      }
      const offsetX = margin + (availW - drawW) / 2;
      const offsetY = margin + (availH - drawH) / 2;
      pdf.addImage(imgData, 'PNG', offsetX, offsetY, drawW, drawH);

      // Add metadata footer
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      const title = doc.metadata.title || 'Untitled';
      pdf.text(`${title} — Generated by Kendraw`, margin, pdfH - 20);

      pdf.save(sanitizeFilename(`${title}.pdf`));
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }, [page, doc.metadata]);

  if (!visible || !properties) return null;

  return (
    <div
      data-testid="properties-panel"
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--kd-color-bg-secondary)',
        borderLeft: '1px solid var(--kd-color-border)',
        padding: 'var(--kd-space-lg)',
        fontSize: 'var(--kd-font-size-sm)',
        overflowY: 'auto',
      }}
    >
      <SectionTitle>Properties</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--kd-space-xs)' }}>
        <PropertyRow label="Formula" value={properties.formula || '—'} copyable />
        <PropertyRow
          label="MW"
          value={properties.molecularWeight > 0 ? `${properties.molecularWeight.toFixed(3)}` : '—'}
        />
        <PropertyRow label="Atoms" value={String(properties.atomCount)} />
        <PropertyRow label="Bonds" value={String(properties.bondCount)} />
        {valenceIssueCount > 0 && (
          <div style={{ color: 'var(--kd-color-warning)', fontSize: 10, marginTop: 2 }}>
            {valenceIssueCount} valence warning{valenceIssueCount > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {backendProps && (
        <>
          <SectionTitle style={{ marginTop: 12 }}>Descriptors</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--kd-space-xs)' }}>
            <PropertyRow
              label="Exact Mass"
              value={backendProps.exact_mass > 0 ? backendProps.exact_mass.toFixed(4) : '—'}
            />
            <PropertyRow
              label="LogP"
              value={backendProps.logp != null ? backendProps.logp.toFixed(2) : '—'}
            />
            <PropertyRow
              label="tPSA"
              value={backendProps.tpsa != null ? `${backendProps.tpsa.toFixed(1)} A²` : '—'}
            />
            <PropertyRow
              label="HBD"
              value={backendProps.hbd != null ? String(backendProps.hbd) : '—'}
            />
            <PropertyRow
              label="HBA"
              value={backendProps.hba != null ? String(backendProps.hba) : '—'}
            />
            <PropertyRow
              label="Rot. Bonds"
              value={
                backendProps.rotatable_bonds != null ? String(backendProps.rotatable_bonds) : '—'
              }
            />
            <PropertyRow
              label="Lipinski"
              value={
                backendProps.lipinski_pass != null
                  ? backendProps.lipinski_pass
                    ? 'PASS'
                    : 'FAIL'
                  : '—'
              }
            />
          </div>

          <SectionTitle style={{ marginTop: 12 }}>Identifiers</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--kd-space-xs)' }}>
            <PropertyRow label="SMILES" value={backendProps.canonical_smiles || '—'} copyable />
            <PropertyRow label="InChI" value={backendProps.inchi || '—'} copyable />
            <PropertyRow label="InChIKey" value={backendProps.inchi_key || '—'} copyable />
          </div>
        </>
      )}

      <SectionTitle style={{ marginTop: 12 }}>Export</SectionTitle>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <ExportButton label="SVG" onClick={handleExportSVG} />
        <ExportButton label="MOL" onClick={handleExportMOL} />
        <ExportButton label="PNG" onClick={handleExportPNG} />
        <ExportButton label="PDF" onClick={handleExportPDF} />
      </div>
    </div>
  );
}

function SectionTitle({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <h3
      style={{
        fontSize: 10,
        color: 'var(--kd-color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 'var(--kd-space-sm)',
        ...style,
      }}
    >
      {children}
    </h3>
  );
}

function PropertyRow({
  label,
  value,
  copyable,
}: {
  label: string;
  value: string;
  copyable?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        lineHeight: 1.6,
      }}
    >
      <span style={{ color: 'var(--kd-color-text-secondary)', fontSize: 11 }}>{label}</span>
      <span
        style={{
          color: 'var(--kd-color-text-primary)',
          fontFamily: 'var(--kd-font-mono)',
          fontSize: 11,
          cursor: copyable ? 'pointer' : 'default',
        }}
        onClick={copyable ? () => void navigator.clipboard.writeText(value) : undefined}
        title={copyable ? 'Click to copy' : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function ExportButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '3px 8px',
        fontSize: 10,
        background: 'var(--kd-color-surface)',
        color: 'var(--kd-color-text-primary)',
        border: '1px solid var(--kd-color-border)',
        borderRadius: 'var(--kd-radius-sm)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function sanitizeFilename(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, '_').trim() || 'molecule';
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = sanitizeFilename(filename);
  a.click();
  URL.revokeObjectURL(url);
}
